import React from 'react';
import { motion } from 'framer-motion';
import { FiBarChart, FiPieChart, FiTrendingUp, FiAlertTriangle, FiCheckCircle, FiClock } from 'react-icons/fi';
import ScrollHeader from '../components/ScrollHeader';
import Carousel from '../components/Carousel';
import ImageModal from '../components/ImageModal';

const Dashboard = () => {
  const [imageModal, setImageModal] = React.useState<{
    isOpen: boolean;
    src: string;
    alt: string;
    title?: string;
  }>({
    isOpen: false,
    src: '',
    alt: '',
    title: ''
  });

  const openImageModal = (src: string, alt: string, title?: string) => {
    setImageModal({ isOpen: true, src, alt, title });
  };

  const closeImageModal = () => {
    setImageModal({ isOpen: false, src: '', alt: '', title: '' });
  };

  const metrics = [
    {
      icon: <FiBarChart />,
      title: "33 Total Projects",
      description: "All projects under security management",
      details: [
        "Shows the scope of your security coverage",
        "Click to see all your projects in detail",
        "Includes active and archived projects",
        "Filter by team, technology, or status"
      ]
    },
    {
      icon: <FiPieChart />,
      title: "120 Total Scans",
      description: "Security scans across all modules combined",
      details: [
        "Indicates your overall security testing activity",
        "Includes all security module scans",
        "Regular scanning keeps you protected",
        "Track scanning frequency and coverage"
      ]
    },
    {
      icon: <FiAlertTriangle />,
      title: "7,479 Total Issues",
      description: "Security issues identified across all projects",
      details: [
        "These are potential vulnerabilities to address",
        "Use ANA Actions to fix them automatically",
        "Prioritize by severity level",
        "Track resolution progress over time"
      ]
    }
  ];

  const scanCategories = [
    { name: "SCA", count: 30, color: "bg-blue-500", description: "Dependency vulnerability scanning" },
    { name: "SBOM", count: 27, color: "bg-green-500", description: "Software component tracking" },
    { name: "Secrets Detection", count: 19, color: "bg-red-500", description: "Credential leak detection" },
    { name: "SAST", count: 19, color: "bg-purple-500", description: "Source code security scanning" },
    { name: "DAST", count: 14, color: "bg-yellow-500", description: "Application runtime testing" },
    { name: "Threat Modeling", count: 10, color: "bg-indigo-500", description: "Security threat analysis" },
    { name: "Infrastructure", count: 1, color: "bg-pink-500", description: "Infrastructure security scanning" }
  ];

  const severityLevels = [
    {
      level: "Critical",
      count: 310,
      color: "text-red-500",
      bgColor: "bg-red-500/10",
      description: "Fix immediately - high risk of exploitation",
      examples: ["Remote code execution", "SQL injection", "Authentication bypass"]
    },
    {
      level: "High",
      count: 1923,
      color: "text-orange-500",
      bgColor: "bg-orange-500/10",
      description: "Fix this week - significant security risk",
      examples: ["Cross-site scripting", "Privilege escalation", "Data exposure"]
    },
    {
      level: "Medium",
      count: 2095,
      color: "text-yellow-500",
      bgColor: "bg-yellow-500/10",
      description: "Fix when convenient - moderate risk",
      examples: ["Information disclosure", "Weak encryption", "CSRF vulnerabilities"]
    },
    {
      level: "Low",
      count: 494,
      color: "text-blue-500",
      bgColor: "bg-blue-500/10",
      description: "Fix during maintenance - minimal immediate risk",
      examples: ["Missing security headers", "Outdated libraries", "Configuration issues"]
    }
  ];

  const dailyTasks = [
    {
      icon: <FiTrendingUp />,
      task: "Check total issues number",
      description: "Is it going up or down?",
      time: "30 seconds"
    },
    {
      icon: <FiPieChart />,
      task: "Review scan categories chart",
      description: "Are you scanning regularly?",
      time: "30 seconds"
    },
    {
      icon: <FiCheckCircle />,
      task: "Look for orange Fix buttons",
      description: "Quick wins for security improvements",
      time: "1 minute"
    }
  ];

  const cardClasses = `
    group p-6 rounded-lg 
    bg-light-card dark:bg-dark-card
    backdrop-blur-sm
    border border-light-border dark:border-dark-border
    transition-all duration-200 
    hover:border-primary-500/30 dark:hover:border-primary-500/30
    hover:shadow-lg hover:shadow-primary-500/5 dark:hover:shadow-primary-500/10
    h-full
  `;

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <ScrollHeader title="Dashboard Overview" />
        
        <div className="text-xl text-gray-600 dark:text-gray-400 mb-8">
          Your security dashboard shows you everything you need to know about your security status at a glance. 
          Think of it as your security health check that updates in real-time.
        </div>
        
        <div className="mb-8">
          <img 
            src="/screenshots/dashboard/02_main-dashboard.png" 
            alt="Main Dashboard"
            className="rounded-lg border border-light-border dark:border-dark-border shadow-lg w-full cursor-pointer hover:shadow-xl transition-shadow"
            onClick={() => openImageModal('/screenshots/dashboard/02_main-dashboard.png', 'Main Dashboard', 'Security Dashboard Overview')}
          />
        </div>

        {/* Key Metrics */}
        <div className="text-xl md:text-3xl font-bold mb-8 text-gray-900 dark:text-white">
          Understanding Your Security Metrics
        </div>
        
        {/* Desktop: Grid layout */}
        <div className="hidden md:grid md:grid-cols-3 gap-6 mb-8">
          {metrics.map((metric, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <div className={cardClasses}>
                <div className="flex items-center gap-3 mb-4">
                  <div className="text-2xl text-primary-500 transition-transform group-hover:scale-110">
                    {metric.icon}
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    {metric.title}
                  </h3>
                </div>
                <p className="text-gray-600 dark:text-gray-400 mb-4">
                  {metric.description}
                </p>
                <ul className="list-disc list-inside space-y-2 text-gray-600 dark:text-gray-400">
                  {metric.details.map((detail, idx) => (
                    <li key={idx} className="text-sm">{detail}</li>
                  ))}
                </ul>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Mobile: Carousel */}
        <div className="md:hidden mb-8">
          <Carousel showDots={true}>
            {metrics.map((metric, index) => (
              <div key={index} className="px-2">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <div className={cardClasses}>
                    <div className="flex items-center gap-3 mb-4">
                      <div className="text-2xl text-primary-500 transition-transform group-hover:scale-110">
                        {metric.icon}
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        {metric.title}
                      </h3>
                    </div>
                    <p className="text-gray-600 dark:text-gray-400 mb-4">
                      {metric.description}
                    </p>
                    <ul className="list-disc list-inside space-y-2 text-gray-600 dark:text-gray-400">
                      {metric.details.map((detail, idx) => (
                        <li key={idx} className="text-sm">{detail}</li>
                      ))}
                    </ul>
                  </div>
                </motion.div>
              </div>
            ))}
          </Carousel>
        </div>

        {/* Scan Categories */}
        <div className="text-xl md:text-3xl font-bold mb-8 text-gray-900 dark:text-white">
          Scan Categories Breakdown
        </div>
        
        <div className="text-xl text-gray-600 dark:text-gray-400 mb-6">
          The colorful chart shows you where your security scanning efforts are focused:
        </div>
        
        <div className="grid grid-cols-1 gap-4 mb-6">
          {scanCategories.map((category, index) => (
            <div key={index} className="flex items-center gap-4 p-4 rounded-lg hover:bg-light-hover dark:hover:bg-dark-hover transition-colors">
              <div className={`w-4 h-4 rounded-full ${category.color} flex-shrink-0`}></div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between mb-1">
                  <span className="font-semibold text-gray-900 dark:text-white">{category.name}</span>
                  <span className="text-xl font-bold text-primary-500">{category.count}</span>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400">{category.description}</p>
              </div>
            </div>
          ))}
        </div>
        
        <div className="p-4 bg-primary-500/5 dark:bg-primary-500/10 rounded-lg border border-primary-500/20 mb-8">
          <p className="text-sm text-gray-700 dark:text-gray-300">
            <strong>Pro Tip:</strong> SCA (Software Composition Analysis) has the most scans because it's often the easiest place to start and find fixable issues!
          </p>
        </div>

        {/* Severity Levels */}
        <div className="text-xl md:text-3xl font-bold mb-8 text-gray-900 dark:text-white">
          Understanding Severity Levels
        </div>
        
        {/* Desktop: Grid layout */}
        <div className="hidden md:grid md:grid-cols-2 gap-6 mb-8">
          {severityLevels.map((severity, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <div className={`p-6 rounded-lg border ${severity.bgColor} border-current h-full`}>
                <div className="flex items-center justify-between mb-3">
                  <h3 className={`text-lg font-semibold ${severity.color}`}>
                    {severity.level}
                  </h3>
                  <span className={`text-2xl font-bold ${severity.color}`}>
                    {severity.count.toLocaleString()}
                  </span>
                </div>
                <p className="text-gray-700 dark:text-gray-300 mb-3">
                  {severity.description}
                </p>
                <div>
                  <p className="text-sm font-semibold text-gray-600 dark:text-gray-400 mb-2">Examples:</p>
                  <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
                    {severity.examples.map((example, idx) => (
                      <li key={idx}>• {example}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Mobile: Carousel */}
        <div className="md:hidden mb-8">
          <Carousel showDots={true}>
            {severityLevels.map((severity, index) => (
              <div key={index} className="px-2">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <div className={`p-6 rounded-lg border ${severity.bgColor} border-current h-full`}>
                    <div className="flex items-center justify-between mb-3">
                      <h3 className={`text-lg font-semibold ${severity.color}`}>
                        {severity.level}
                      </h3>
                      <span className={`text-2xl font-bold ${severity.color}`}>
                        {severity.count.toLocaleString()}
                      </span>
                    </div>
                    <p className="text-gray-700 dark:text-gray-300 mb-3">
                      {severity.description}
                    </p>
                    <div>
                      <p className="text-sm font-semibold text-gray-600 dark:text-gray-400 mb-2">Examples:</p>
                      <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
                        {severity.examples.map((example, idx) => (
                          <li key={idx}>• {example}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </motion.div>
              </div>
            ))}
          </Carousel>
        </div>

        {/* Daily Security Check */}
        <div className="text-xl md:text-3xl font-bold mb-8 text-gray-900 dark:text-white">
          Daily Security Check (2 minutes)
        </div>
        
        {/* Desktop: Grid layout */}
        <div className="hidden md:grid md:grid-cols-3 gap-6 mb-8">
          {dailyTasks.map((task, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <div className={cardClasses}>
                <div className="text-2xl text-primary-500 mb-3 transition-transform group-hover:scale-110">
                  {task.icon}
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                  {task.task}
                </h3>
                <p className="text-gray-600 dark:text-gray-400 mb-3">
                  {task.description}
                </p>
                <div className="flex items-center gap-2">
                  <FiClock className="w-4 h-4 text-gray-500" />
                  <span className="text-sm text-gray-500">{task.time}</span>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Mobile: Carousel */}
        <div className="md:hidden mb-8">
          <Carousel showDots={true}>
            {dailyTasks.map((task, index) => (
              <div key={index} className="px-2">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <div className={cardClasses}>
                    <div className="text-2xl text-primary-500 mb-3 transition-transform group-hover:scale-110">
                      {task.icon}
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                      {task.task}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400 mb-3">
                      {task.description}
                    </p>
                    <div className="flex items-center gap-2">
                      <FiClock className="w-4 h-4 text-gray-500" />
                      <span className="text-sm text-gray-500">{task.time}</span>
                    </div>
                  </div>
                </motion.div>
              </div>
            ))}
          </Carousel>
        </div>

        {/* Navigation Guide */}
        <div className="text-xl md:text-3xl font-bold mb-8 text-gray-900 dark:text-white">
          Dashboard Navigation Guide
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Quick Actions
            </h3>
            <ul className="space-y-3">
              <li className="flex items-start gap-2">
                <FiCheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                <div>
                  <span className="font-medium text-gray-900 dark:text-white">Start a New Scan:</span>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Click any module → "New Scan" button → Select project</p>
                </div>
              </li>
              <li className="flex items-start gap-2">
                <FiCheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                <div>
                  <span className="font-medium text-gray-900 dark:text-white">Fix Issues Fast:</span>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Click "SCA" → Look for orange "Fix" buttons → Let ANA create PRs</p>
                </div>
              </li>
              <li className="flex items-start gap-2">
                <FiCheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                <div>
                  <span className="font-medium text-gray-900 dark:text-white">Check Recent Activity:</span>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Review "Last updated" timestamps and scan history</p>
                </div>
              </li>
            </ul>
          </div>
          
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Best Practices
            </h3>
            <ul className="list-disc list-inside space-y-2 text-gray-600 dark:text-gray-400">
              <li>Focus on High-Impact: Start with SCA, address Critical/High severity first</li>
              <li>Track Progress: Take weekly screenshots, compare issue counts over time</li>
              <li>Stay Current: Check daily, scan before releases, keep integrations connected</li>
            </ul>
          </div>
        </div>

        {/* Next Steps */}
        <div className="text-center py-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <h2 className="text-2xl font-bold mb-4 text-gray-900 dark:text-white">
              Ready to Take Action?
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Start with these recommended next steps to improve your security posture.
            </p>
            
            {/* Desktop: Grid layout */}
            <div className="hidden md:grid md:grid-cols-3 gap-4">
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <a 
                  href="/ana-actions" 
                  className="block p-4 bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border rounded-lg hover:border-primary-500/30 transition-colors"
                >
                  <FiCheckCircle className="w-6 h-6 text-primary-500 mx-auto mb-2" />
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-1 text-center">Start Fixing Issues</h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400 text-center">Learn about ANA Actions for automated fixes</p>
                </a>
              </motion.div>
              
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <a 
                  href="/sca" 
                  className="block p-4 bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border rounded-lg hover:border-primary-500/30 transition-colors"
                >
                  <FiBarChart className="w-6 h-6 text-primary-500 mx-auto mb-2" />
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-1 text-center">Explore SCA Scanning</h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400 text-center">Tackle your biggest issue category</p>
                </a>
              </motion.div>
              
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <a 
                  href="/integrations" 
                  className="block p-4 bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border rounded-lg hover:border-primary-500/30 transition-colors"
                >
                  <FiTrendingUp className="w-6 h-6 text-primary-500 mx-auto mb-2" />
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-1 text-center">Set Up Integrations</h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400 text-center">Enable automated scanning workflows</p>
                </a>
              </motion.div>
            </div>

            {/* Mobile: Carousel */}
            <div className="md:hidden">
              <Carousel showDots={true}>
                <div className="px-2">
                  <a 
                    href="/ana-actions" 
                    className="block p-4 bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border rounded-lg hover:border-primary-500/30 transition-colors"
                  >
                    <FiCheckCircle className="w-6 h-6 text-primary-500 mx-auto mb-2" />
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-1 text-center">Start Fixing Issues</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400 text-center">Learn about ANA Actions for automated fixes</p>
                  </a>
                </div>
                
                <div className="px-2">
                  <a 
                    href="/sca" 
                    className="block p-4 bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border rounded-lg hover:border-primary-500/30 transition-colors"
                  >
                    <FiBarChart className="w-6 h-6 text-primary-500 mx-auto mb-2" />
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-1 text-center">Explore SCA Scanning</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400 text-center">Tackle your biggest issue category</p>
                  </a>
                </div>
                
                <div className="px-2">
                  <a 
                    href="/integrations" 
                    className="block p-4 bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border rounded-lg hover:border-primary-500/30 transition-colors"
                  >
                    <FiTrendingUp className="w-6 h-6 text-primary-500 mx-auto mb-2" />
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-1 text-center">Set Up Integrations</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400 text-center">Enable automated scanning workflows</p>
                  </a>
                </div>
              </Carousel>
            </div>
          </motion.div>
        </div>
      </motion.div>

      {/* Image Modal */}
      <ImageModal
        isOpen={imageModal.isOpen}
        onClose={closeImageModal}
        src={imageModal.src}
        alt={imageModal.alt}
        title={imageModal.title}
      />
    </div>
  );
};

export default Dashboard;
